import React, { useEffect, useRef, useState } from "react";
import jsonExport from "jsonexport/dist";

import { API, graphqlOperation } from "aws-amplify";
import { addMonths, endOfMonth, format, startOfMonth } from "date-fns";

import {
  Box,
  Card,
  Grid,
  makeStyles,
  TextField as FilterField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@material-ui/core";

import {
  Button,
  Datagrid,
  downloadCSV,
  FunctionField,
  ListContextProvider,
  NumberField,
  SimpleForm,
  SimpleList,
  TextField,
  Title,
  TopToolbar,
  useList,
  useNotify,
} from "react-admin";

import {
  searchInboundRailcars,
  searchOutboundRailcars,
} from "../../../graphql/report-queries";
import {
  ExportIcon,
  InboundIcon,
  LaunchIcon,
  OutboundIcon,
  SitIcon,
} from "../../../components/common/iconography";
import { AccountDropDown } from "../../../components/common/AccountDropDown";
import { BackButton } from "../../../components/common/BackButton";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { YardDateField } from "../../../components/common/YardDateField";
import { Pagination } from "../../../components/Pagination";
import { logError } from "../../../utils/appUtils";
import { formatInYardTimeZone, getYardNow } from "../../../utils/dateUtils";
import { deepRedirect, getBackPath } from "../../../utils/utils";
import {
  buildRailcarFilter,
  getPeriodBounds,
  isOnSiteDuring,
  RailcarDirection,
  summarizeRailcars,
  toInboundRailcarRow,
  toOutboundRailcarRow,
} from "./railcarsOnSite";

const useStyles = makeStyles((theme) => ({
  summary: {
    padding: theme.spacing(2, 2, 0, 2),
  },
}));

const DATE_FORMAT = "yyyy-MM-dd";
const DATE_TIME_FORMAT = "MM/dd/yyyy hh:mm a";

const formatYardDateTime = (value) =>
  value ? formatInYardTimeZone(new Date(value), DATE_TIME_FORMAT) : "";

// Last full month on the yard's calendar, the period customers ask about most.
const getDefaultPeriod = () => {
  const lastMonth = addMonths(getYardNow(), -1);
  return {
    startDate: format(startOfMonth(lastMonth), DATE_FORMAT),
    endDate: format(endOfMonth(lastMonth), DATE_FORMAT),
  };
};

// Every page of a search. Sorted on id so each page picks up exactly where
// the last one ended.
const searchAll = async (query, resultKey, filter) => {
  const limit = 1000;
  const items = [];
  let nextToken = null;
  do {
    const input = { filter, sort: { field: "id", direction: "asc" }, limit };
    if (nextToken) input.nextToken = nextToken;
    const result = await API.graphql(graphqlOperation(query, input));
    const page = result.data[resultKey];
    items.push(...page.items);
    nextToken = page.items.length < limit ? null : page.nextToken;
  } while (nextToken);
  return items;
};

const exporter = (rows, startDate, endDate) => {
  const rowsForExport = rows.map((row) => ({
    direction:
      row.direction === RailcarDirection.INBOUND ? "Inbound" : "Outbound",
    railCarNumber: row.railCarNumber || "",
    accountName: row.accountName || "",
    carrierName: row.carrierName || "",
    sit: row.sit ? "Yes" : "",
    status: row.statusLabel || "",
    arrivedAt: formatYardDateTime(row.arrivedAt),
    placedAt: formatYardDateTime(row.placedAt),
    releasedAt: formatYardDateTime(row.releasedAt),
    pulledAt: formatYardDateTime(row.pulledAt),
    daysOnSite: row.daysOnSite ?? "",
  }));

  jsonExport(
    rowsForExport,
    {
      rename: [
        "Direction",
        "Car/Trl Number",
        "Account",
        "Carrier",
        "SIT",
        "Status",
        "Arrived",
        "Placed",
        "Released",
        "Pulled",
        "Days on Site",
      ],
    },
    (err, csv) => {
      downloadCSV(csv, `railcarsProcessed_${startDate}_${endDate}`);
    },
  );
};

const ListActions = ({
  finalData,
  setAccountId,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  periodIsValid,
  loading,
  runReport,
}) => (
  <Box mt={-4} mb={1}>
    <TopToolbar>
      <Button
        label="Export"
        disabled={finalData.length === 0}
        onClick={() => exporter(finalData, startDate, endDate)}
      >
        <ExportIcon />
      </Button>
      <BackButton basePath="/reports" />
    </TopToolbar>
    <Grid container spacing={1}>
      <Grid item xs={12} md={4}>
        <AccountDropDown
          source="accountId"
          label="Account"
          onAccountChange={(value) => setAccountId(value?.id || "")}
          helperText={false}
        />
      </Grid>
      <Grid item xs={6} md={3}>
        <FilterField
          id="startDate"
          type="date"
          label="From"
          variant="outlined"
          margin="dense"
          fullWidth
          InputLabelProps={{ shrink: true }}
          value={startDate}
          onChange={(evt) => setStartDate(evt?.target?.value || "")}
        />
      </Grid>
      <Grid item xs={6} md={3}>
        <FilterField
          id="endDate"
          type="date"
          label="To"
          variant="outlined"
          margin="dense"
          fullWidth
          InputLabelProps={{ shrink: true }}
          value={endDate}
          error={Boolean(startDate && endDate && !periodIsValid)}
          onChange={(evt) => setEndDate(evt?.target?.value || "")}
        />
      </Grid>
      <Grid item xs={12} md={2}>
        <Box mt={1}>
          <Button
            label="Run"
            variant="contained"
            color="default"
            disabled={!periodIsValid || loading}
            onClick={runReport}
          />
        </Box>
      </Grid>
    </Grid>
  </Box>
);

// /reportRailcarsProcessed
export const RailcarsProcessedReport = (props) => {
  const classes = useStyles();
  const notify = useNotify();

  const [accountId, setAccountId] = useState("");
  const [startDate, setStartDate] = useState(
    () => getDefaultPeriod().startDate,
  );
  const [endDate, setEndDate] = useState(() => getDefaultPeriod().endDate);

  const [finalData, setFinalData] = useState([]);
  const [hasRun, setHasRun] = useState(false);
  const [loading, setLoading] = useState(false);

  // yyyy-MM-dd strings compare in calendar order.
  const periodIsValid = Boolean(startDate && endDate && startDate <= endDate);

  // Bumped on every run and every criteria change. A run that finishes after
  // either drops its rows, so the grid and export never show results for
  // criteria the fields no longer hold.
  const requestRef = useRef(0);

  useEffect(() => {
    requestRef.current += 1;
    setFinalData([]);
    setHasRun(false);
    setLoading(false);
  }, [accountId, startDate, endDate]);

  const runReport = async () => {
    const requestId = ++requestRef.current;
    const isCurrent = () => requestId === requestRef.current;
    try {
      setLoading(true);
      const { startIso, endIso } = getPeriodBounds(startDate, endDate);
      const params = {
        startIso,
        endIso,
        reloadYardId: window.appUser?.reloadYardId,
        accountId,
      };

      const [inbounds, outbounds] = await Promise.all([
        searchAll(
          searchInboundRailcars,
          "searchInboundShipments",
          buildRailcarFilter(RailcarDirection.INBOUND, params),
        ),
        searchAll(
          searchOutboundRailcars,
          "searchOutboundShipments",
          buildRailcarFilter(RailcarDirection.OUTBOUND, params),
        ),
      ]);

      const now = new Date();
      const rows = [
        ...inbounds.map((shipment) => toInboundRailcarRow(shipment, now)),
        ...outbounds.map((shipment) => toOutboundRailcarRow(shipment, now)),
      ]
        .filter((row) => isOnSiteDuring(row, startIso, endIso))
        .sort((a, b) => a.onSiteFrom.localeCompare(b.onSiteFrom));

      if (!isCurrent()) return;
      setFinalData(rows);
      setHasRun(true);
    } catch (err) {
      if (!isCurrent()) return;
      logError("RailcarsProcessedReport:runReport", err, notify);
      setFinalData([]);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  const listContext = useList({
    ...props,
    data: finalData,
    ids: finalData.map(({ id }) => id),
    loading,
    loaded: !loading,
    filter: "",
    sort: { field: "onSiteFrom", order: "ASC" },
    perPage: 25,
  });

  const totals = summarizeRailcars(finalData);

  return (
    <SimpleForm toolbar={false}>
      <ListContextProvider value={listContext}>
        <Title title={<ForceTitle title="Railcars Processed" />} />
        <ListActions
          finalData={finalData}
          setAccountId={setAccountId}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          periodIsValid={periodIsValid}
          loading={loading}
          runReport={runReport}
        />
        <Card>
          <div className={classes.summary}>
            {hasRun && (
              <Typography>
                {`${totals.total} railcars: ${totals.inbound} inbound (${totals.sit} SIT), ${totals.outbound} outbound. ${totals.onSite} still on site.`}
              </Typography>
            )}
            <Typography variant="caption" color="textSecondary">
              Rail cars on site at any time in the period, in any status:
              arrived before the period ended and not yet released or pulled
              when it began.
            </Typography>
          </div>
          <RailcarGrid
            accountId={accountId}
            history={props.history}
            backPath={getBackPath({ ...props, basePath: "/reports" })}
          />
          <Pagination {...listContext} />
        </Card>
      </ListContextProvider>
    </SimpleForm>
  );
};

const DirectionIcon = ({ record }) =>
  record?.direction === RailcarDirection.INBOUND ? (
    <Tooltip title="Inbound">
      <InboundIcon />
    </Tooltip>
  ) : (
    <Tooltip title="Outbound">
      <OutboundIcon />
    </Tooltip>
  );

const shipmentPath = (record) =>
  record.direction === RailcarDirection.INBOUND
    ? `/inboundShipments/${record.id}`
    : `/outboundShipments/${record.id}`;

// deepRedirect rather than a plain link so the shipment's Back button returns
// to this report instead of the shipment list.
const OpenButton = ({ record, history, backPath }) => (
  <Button
    label="Open"
    onClick={(event) => {
      event.stopPropagation();
      deepRedirect(history, shipmentPath(record), backPath, {});
    }}
  >
    <LaunchIcon />
  </Button>
);

const RailcarGrid = ({ accountId, history, backPath, ...props }) => {
  const isXSmall = useMediaQuery((theme) => theme.breakpoints.down("xs"));

  return isXSmall ? (
    <SimpleList
      {...props}
      leftIcon={(record) => <DirectionIcon record={record} />}
      primaryText={(record) =>
        `${record.railCarNumber || ""}${record.sit ? " (SIT)" : ""}`
      }
      secondaryText={(record) =>
        `${formatYardDateTime(record.onSiteFrom)} – ${
          record.onSiteUntil
            ? formatYardDateTime(record.onSiteUntil)
            : "on site"
        }`
      }
      tertiaryText={(record) => `${record.daysOnSite ?? 0} Days`}
      rightIcon={(record) => (
        <OpenButton record={record} history={history} backPath={backPath} />
      )}
      linkType={false}
    />
  ) : (
    <Datagrid {...props}>
      <FunctionField
        source="direction"
        label=""
        render={(record) => <DirectionIcon record={record} />}
      />
      <TextField source="railCarNumber" label="Car/Trl No" />
      {!accountId && <TextField source="accountName" label="Account" />}
      <TextField source="carrierName" label="Carrier" />
      <FunctionField
        source="sit"
        label="SIT"
        render={(record) =>
          record.sit ? (
            // SitIcon is a plain function component; the span takes the
            // Tooltip's ref.
            <Tooltip title="Storage in Transit">
              <span>
                <SitIcon />
              </span>
            </Tooltip>
          ) : null
        }
      />
      <TextField source="statusLabel" label="Status" />
      <YardDateField source="arrivedAt" label="Arrived" showTime={true} />
      <YardDateField source="placedAt" label="Placed" showTime={true} />
      <YardDateField source="releasedAt" label="Released" showTime={true} />
      <YardDateField source="pulledAt" label="Pulled" showTime={true} />
      <NumberField source="daysOnSite" label="Days on Site" />
      <FunctionField
        label=""
        render={(record) => (
          <OpenButton record={record} history={history} backPath={backPath} />
        )}
      />
    </Datagrid>
  );
};
